import { NetInfoStateType } from '@react-native-community/netinfo';

import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import { defaultLanguage } from 'utils/Language/i18n';

import { RequestStatus } from '../utils';
import {
    accountInfoSuccess,
    addProfileFailure,
    addProfileSuccess,
    changeUserProfileAvatar,
    changeUserProfileFailure,
    changeUserProfileRating,
    changeUserProfileSuccess,
    getEntitlementsFailure,
    getEntitlementsSuccess,
    getFavouritesChannelsListFailure,
    getFavouritesChannelsListSuccess,
    getPinsStatusFailure,
    getPinsStatusSuccess,
    getProfileSuccess,
    getProfilesListSuccess,
    loginFailure,
    loginSilentlyFailure,
    loginSuccess,
    logoutFailure,
    setCashedPin,
    setConnectionType,
    setCurrentProfileAvatar,
    setLanguage,
    setLastProvisionDate,
    setLineupChanged,
    setModalVisible,
    setOTTLastConnection,
    setRefreshTokenError,
    setShowLineupModal,
    setSuspensionType,
    updateFavouritesChannelsListFailure,
    updateFavouritesChannelsListSuccess,
    updateRatingsThresholdsFailure,
    updateRatingsThresholdsSuccess,
} from './actions';
import {
    ACCOUNT_INFO_SUCCESS,
    ADD_PROFILE_FAILURE,
    ADD_PROFILE_REQUEST,
    ADD_PROFILE_SUCCESS,
    CHANGE_USER_PROFILE_FAILURE,
    CHANGE_USER_PROFILE_REQUEST,
    CHANGE_USER_PROFILE_SUCCESS,
    DELETE_FAVOURITES_CHANNELS_LIST_FAILURE,
    DELETE_FAVOURITES_CHANNELS_LIST_REQUEST,
    DELETE_FAVOURITES_CHANNELS_LIST_SUCCESS,
    DELETE_PROFILE_FAILURE,
    DELETE_PROFILE_REQUEST,
    DELETE_PROFILE_REQUEST_RESET,
    DELETE_PROFILE_SUCCESS,
    EDIT_PROFILE_FAILURE,
    EDIT_PROFILE_REQUEST,
    EDIT_PROFILE_REQUEST_RESET,
    EDIT_PROFILE_SUCCESS,
    GET_ENTITLEMENTS_FAILURE,
    GET_ENTITLEMENTS_REQUEST,
    GET_ENTITLEMENTS_SUCCESS,
    GET_FAVOURITES_CHANNELS_LIST_FAILURE,
    GET_FAVOURITES_CHANNELS_LIST_REQUEST,
    GET_FAVOURITES_CHANNELS_LIST_SUCCESS,
    GET_PINS_STATUS_FAILURE,
    GET_PINS_STATUS_REQUEST,
    GET_PINS_STATUS_SUCCESS,
    GET_PROFILES_LIST_FAILURE,
    GET_PROFILES_LIST_REQUEST,
    GET_PROFILES_LIST_SUCCESS,
    GET_PROFILE_SUCCESS,
    LOGIN_ERROR_RESET,
    LOGIN_FAILURE,
    LOGIN_REQUEST,
    LOGIN_SILENTLY_FAILURE,
    LOGIN_SILENTLY_SUCCESS,
    LOGIN_SUCCESS,
    LOGOUT_FAILURE,
    LOGOUT_SUCCESS,
    RESET_CASHED_PINS,
    RESET_CLIENT_ID,
    SELECT_USER_PROFILE_AVATAR,
    SELECT_USER_PROFILE_RATING,
    SET_CASHED_PIN,
    SET_CONNECTION_TYPE,
    SET_CURRENT_PROFILE_AVATAR,
    SET_LANGUAGE,
    SET_LAST_PROVISION_DATE,
    SET_LINEUP_CHANGED,
    SET_MODAL_VISIBLE,
    SET_OTT_LAST_CONNECTION,
    SET_REFRESH_TOKEN_ERROR,
    SET_SHOW_LINEUP_MODAL,
    SET_SUSPENSION_TYPE,
    UPDATE_FAVOURITES_CHANNELS_LIST_FAILURE,
    UPDATE_FAVOURITES_CHANNELS_LIST_REQUEST,
    UPDATE_FAVOURITES_CHANNELS_LIST_SUCCESS,
    UPDATE_RATINGS_THRESHOLDS_CLEAN_REQUEST,
    UPDATE_RATINGS_THRESHOLDS_FAILURE,
    UPDATE_RATINGS_THRESHOLDS_REQUEST,
    UPDATE_RATINGS_THRESHOLDS_SUCCESS,
} from './constants';
import { UserState } from './reducer.types';

const initialState: UserState = {
    language: defaultLanguage,
    loginRequest: {
        status: RequestStatus.IDLE,
        error: undefined,
    },
    loginSilentlyRequest: {
        status: RequestStatus.IDLE,
        error: null,
    },
    logoutRequest: {
        status: RequestStatus.IDLE,
        error: null,
    },
    selectedProfile: undefined,
    isGuest: false,
    auth: undefined,
    clientId: undefined,
    entitlementsRequest: {
        status: RequestStatus.IDLE,
        error: null,
        data: [],
    },
    updateRatingsThresholdsRequest: {
        status: RequestStatus.IDLE,
        error: null,
    },
    modalVisible: false,
    pinsStatusRequest: {
        status: RequestStatus.IDLE,
        error: null,
        data: [],
    },
    refreshTokenError: null,
    favouriteChannelsListRequest: {
        status: RequestStatus.IDLE,
        error: null,
        data: [],
    },
    updateFavouriteChannelsListRequest: {
        status: RequestStatus.IDLE,
        error: null,
    },
    profilesListRequest: {
        status: RequestStatus.IDLE,
        error: null,
        data: [],
    },
    changeUserProfileRequest: {
        status: RequestStatus.IDLE,
        error: null,
    },
    addProfileRequest: {
        status: RequestStatus.IDLE,
        error: null,
    },
    profileParentalRating: null,
    profileAvatar: null,
    currentProfileAvatar: undefined,
    editProfileRequest: {
        status: RequestStatus.IDLE,
        error: null,
    },
    deleteProfileRequest: {
        status: RequestStatus.IDLE,
        error: null,
    },
    OTTLastConnectionTime: undefined,
    lastProvisionDate: undefined,
    suspension: undefined,
    cashedPinByParentalRating: undefined,
    isLineupChanged: false,
    shouldLineupModalShow: false,
    connectionType: NetInfoStateType.wifi,
    sharedConsentPublicId: undefined,
};

const handlers = {
    [SET_LANGUAGE](
        state: UserState,
        { payload: { language } }: ReturnType<typeof setLanguage>,
    ): UserState {
        return {
            ...state,
            language,
        };
    },
    [LOGIN_REQUEST](state: UserState): UserState {
        return {
            ...state,
            loginRequest: {
                ...state.loginRequest,
                status: RequestStatus.PENDING,
                error: undefined,
            },
        };
    },
    [LOGIN_SUCCESS](
        state: UserState,
        {
            payload: { auth, currentUserProfileId },
        }: ReturnType<typeof loginSuccess>,
    ): UserState {
        return {
            ...state,
            loginRequest: {
                ...state.loginRequest,
                status: RequestStatus.SUCCESS,
                error: undefined,
            },
            auth,
            clientId: auth.client_id,
            selectedProfile: currentUserProfileId || state.selectedProfile,
            refreshTokenError: null,
        };
    },
    [LOGIN_FAILURE](
        state: UserState,
        { payload: { error } }: ReturnType<typeof loginFailure>,
    ): UserState {
        return {
            ...state,
            loginRequest: {
                status: RequestStatus.FAILURE,
                error,
            },
        };
    },
    [LOGIN_ERROR_RESET](state: UserState): UserState {
        return {
            ...state,
            loginRequest: {
                status: RequestStatus.IDLE,
                error: undefined,
            },
        };
    },
    [LOGIN_SILENTLY_FAILURE](
        state: UserState,
        { payload: { error } }: ReturnType<typeof loginSilentlyFailure>,
    ): UserState {
        return {
            ...state,
            ...initialState,
            auth: isErrorNeedsRetry(error) ? state.auth : initialState.auth,
            language: state.language,
            loginSilentlyRequest: {
                ...state.loginSilentlyRequest,
                status: RequestStatus.FAILURE,
                error,
            },
            clientId: state.clientId,
            selectedProfile: state.selectedProfile,
            suspension: state.suspension,
            profilesListRequest: state.profilesListRequest,
            cashedPinByParentalRating: state.cashedPinByParentalRating,
        };
    },
    [LOGIN_SILENTLY_SUCCESS](state: UserState): UserState {
        return {
            ...state,
            ...initialState,
            language: state.language,
            loginSilentlyRequest: {
                ...state.loginSilentlyRequest,
                status: RequestStatus.SUCCESS,
                error: null,
            },
            clientId: state.clientId,
            selectedProfile: state.selectedProfile,
            entitlementsRequest: state.entitlementsRequest,
            profilesListRequest: state.profilesListRequest,
            suspension: state.suspension,
            cashedPinByParentalRating: state.cashedPinByParentalRating,
            favouriteChannelsListRequest: state.favouriteChannelsListRequest,
            connectionType: state.connectionType,
        };
    },
    [SET_REFRESH_TOKEN_ERROR](
        state: UserState,
        { payload: { error } }: ReturnType<typeof setRefreshTokenError>,
    ): UserState {
        return {
            ...state,
            refreshTokenError: error,
        };
    },

    [LOGOUT_SUCCESS](state: UserState): UserState {
        return {
            ...initialState,
            language: state.language,
            clientId: state.clientId,
            refreshTokenError: state.refreshTokenError,
            profilesListRequest: state.profilesListRequest,
            suspension: state.suspension,
            cashedPinByParentalRating: state.cashedPinByParentalRating,
            connectionType: state.connectionType,
        };
    },
    [LOGOUT_FAILURE](
        state: UserState,
        { payload: { error } }: ReturnType<typeof logoutFailure>,
    ): UserState {
        return {
            ...state,
            logoutRequest: {
                ...state.logoutRequest,
                status: RequestStatus.FAILURE,
                error,
            },
        };
    },

    [GET_ENTITLEMENTS_REQUEST](state: UserState): UserState {
        return {
            ...state,
            entitlementsRequest: {
                ...state.entitlementsRequest,
                status: RequestStatus.PENDING,
                error: null,
            },
        };
    },
    [GET_ENTITLEMENTS_SUCCESS](
        state: UserState,
        {
            payload: { entitlements },
        }: ReturnType<typeof getEntitlementsSuccess>,
    ): UserState {
        return {
            ...state,
            entitlementsRequest: {
                ...state.entitlementsRequest,
                status: RequestStatus.SUCCESS,
                error: null,
                data: entitlements,
            },
        };
    },
    [GET_ENTITLEMENTS_FAILURE](
        state: UserState,
        { payload: { error } }: ReturnType<typeof getEntitlementsFailure>,
    ): UserState {
        return {
            ...state,
            entitlementsRequest: {
                ...state.entitlementsRequest,
                status: RequestStatus.FAILURE,
                error,
            },
        };
    },

    [UPDATE_RATINGS_THRESHOLDS_REQUEST](state: UserState): UserState {
        return {
            ...state,
            updateRatingsThresholdsRequest: {
                ...state.updateRatingsThresholdsRequest,
                status: RequestStatus.PENDING,
                error: null,
            },
        };
    },
    [UPDATE_RATINGS_THRESHOLDS_SUCCESS](
        state: UserState,
        {
            payload: { rating },
        }: ReturnType<typeof updateRatingsThresholdsSuccess>,
    ): UserState {
        return {
            ...state,
            updateRatingsThresholdsRequest: {
                ...state.updateRatingsThresholdsRequest,
                status: RequestStatus.SUCCESS,
                error: null,
            },
            auth: state.auth
                ? {
                      ...state.auth,
                      parentalControl: { ratingThreshold: { HCRS: rating } },
                  }
                : state.auth,
        };
    },
    [UPDATE_RATINGS_THRESHOLDS_FAILURE](
        state: UserState,
        {
            payload: { error },
        }: ReturnType<typeof updateRatingsThresholdsFailure>,
    ): UserState {
        return {
            ...state,
            updateRatingsThresholdsRequest: {
                ...state.updateRatingsThresholdsRequest,
                status: RequestStatus.FAILURE,
                error,
            },
        };
    },
    [UPDATE_RATINGS_THRESHOLDS_CLEAN_REQUEST](state: UserState): UserState {
        return {
            ...state,
            updateRatingsThresholdsRequest: {
                ...state.updateRatingsThresholdsRequest,
                status: RequestStatus.IDLE,
                error: null,
            },
        };
    },
    [SET_MODAL_VISIBLE](
        state: UserState,
        { payload: { visible } }: ReturnType<typeof setModalVisible>,
    ): UserState {
        return {
            ...state,
            modalVisible: visible,
        };
    },
    [GET_PINS_STATUS_REQUEST](state: UserState): UserState {
        return {
            ...state,
            pinsStatusRequest: {
                status: RequestStatus.PENDING,
                error: null,
                data: [],
            },
        };
    },
    [GET_PINS_STATUS_SUCCESS](
        state: UserState,
        { payload: { pinsStatus } }: ReturnType<typeof getPinsStatusSuccess>,
    ): UserState {
        return {
            ...state,
            pinsStatusRequest: {
                status: RequestStatus.SUCCESS,
                error: null,
                data: pinsStatus,
            },
        };
    },
    [GET_PINS_STATUS_FAILURE](
        state: UserState,
        { payload: { error } }: ReturnType<typeof getPinsStatusFailure>,
    ): UserState {
        return {
            ...state,
            pinsStatusRequest: {
                status: RequestStatus.FAILURE,
                data: [],
                error,
            },
        };
    },

    [GET_FAVOURITES_CHANNELS_LIST_REQUEST](state: UserState): UserState {
        return {
            ...state,
            favouriteChannelsListRequest: {
                ...state.favouriteChannelsListRequest,
                status: RequestStatus.PENDING,
                error: null,
            },
        };
    },
    [GET_FAVOURITES_CHANNELS_LIST_SUCCESS](
        state: UserState,
        {
            payload: { favouritesLists },
        }: ReturnType<typeof getFavouritesChannelsListSuccess>,
    ): UserState {
        return {
            ...state,
            favouriteChannelsListRequest: {
                ...state.favouriteChannelsListRequest,
                status: RequestStatus.SUCCESS,
                error: null,
                data: favouritesLists,
            },
        };
    },
    [GET_FAVOURITES_CHANNELS_LIST_FAILURE](
        state: UserState,
        {
            payload: { error },
        }: ReturnType<typeof getFavouritesChannelsListFailure>,
    ): UserState {
        return {
            ...state,
            favouriteChannelsListRequest: {
                ...state.favouriteChannelsListRequest,
                status: RequestStatus.FAILURE,
                data: [],
                error,
            },
        };
    },

    [UPDATE_FAVOURITES_CHANNELS_LIST_REQUEST](state: UserState): UserState {
        return {
            ...state,
            updateFavouriteChannelsListRequest: {
                ...state.updateFavouriteChannelsListRequest,
                status: RequestStatus.PENDING,
                error: null,
            },
        };
    },
    [UPDATE_FAVOURITES_CHANNELS_LIST_SUCCESS](
        state: UserState,
        {
            payload: { updatedFavouritesList },
        }: ReturnType<typeof updateFavouritesChannelsListSuccess>,
    ): UserState {
        return {
            ...state,
            updateFavouriteChannelsListRequest: {
                ...state.updateFavouriteChannelsListRequest,
                status: RequestStatus.SUCCESS,
                error: null,
            },
            favouriteChannelsListRequest: {
                ...state.favouriteChannelsListRequest,
                data: state.favouriteChannelsListRequest.data.map((list) => {
                    if (list.id === updatedFavouritesList.id) {
                        return updatedFavouritesList;
                    }

                    return list;
                }),
            },
        };
    },
    [UPDATE_FAVOURITES_CHANNELS_LIST_FAILURE](
        state: UserState,
        {
            payload: { error },
        }: ReturnType<typeof updateFavouritesChannelsListFailure>,
    ): UserState {
        return {
            ...state,
            updateFavouriteChannelsListRequest: {
                ...state.updateFavouriteChannelsListRequest,
                status: RequestStatus.FAILURE,
                error,
            },
        };
    },

    [DELETE_FAVOURITES_CHANNELS_LIST_REQUEST](state: UserState): UserState {
        return {
            ...state,
            updateFavouriteChannelsListRequest: {
                ...state.updateFavouriteChannelsListRequest,
                status: RequestStatus.PENDING,
                error: null,
            },
        };
    },
    [DELETE_FAVOURITES_CHANNELS_LIST_SUCCESS](state: UserState): UserState {
        return {
            ...state,
            updateFavouriteChannelsListRequest: {
                ...state.updateFavouriteChannelsListRequest,
                status: RequestStatus.SUCCESS,
                error: null,
            },
            favouriteChannelsListRequest: {
                ...state.favouriteChannelsListRequest,
                data: [],
            },
        };
    },
    [DELETE_FAVOURITES_CHANNELS_LIST_FAILURE](
        state: UserState,
        {
            payload: { error },
        }: ReturnType<typeof updateFavouritesChannelsListFailure>,
    ): UserState {
        return {
            ...state,
            updateFavouriteChannelsListRequest: {
                ...state.updateFavouriteChannelsListRequest,
                status: RequestStatus.FAILURE,
                error,
            },
        };
    },

    [GET_PROFILES_LIST_REQUEST](state: UserState): UserState {
        return {
            ...state,
            profilesListRequest: {
                ...state.profilesListRequest,
                status: RequestStatus.PENDING,
                error: null,
            },
        };
    },
    [GET_PROFILES_LIST_SUCCESS](
        state: UserState,
        {
            payload: { profilesLists },
        }: ReturnType<typeof getProfilesListSuccess>,
    ): UserState {
        return {
            ...state,
            profilesListRequest: {
                ...state.profilesListRequest,
                status: RequestStatus.SUCCESS,
                error: null,
                data: profilesLists,
            },
        };
    },
    [GET_PROFILES_LIST_FAILURE](
        state: UserState,
        {
            payload: { error },
        }: ReturnType<typeof getFavouritesChannelsListFailure>,
    ): UserState {
        return {
            ...state,
            profilesListRequest: {
                ...state.profilesListRequest,
                status: RequestStatus.FAILURE,
                error,
            },
        };
    },

    [CHANGE_USER_PROFILE_REQUEST](state: UserState): UserState {
        return {
            ...state,
            changeUserProfileRequest: {
                ...state.changeUserProfileRequest,
                status: RequestStatus.PENDING,
                error: null,
            },
        };
    },
    [CHANGE_USER_PROFILE_SUCCESS](
        state: UserState,
        {
            payload: { auth, currentUserProfileId },
        }: ReturnType<typeof changeUserProfileSuccess>,
    ): UserState {
        return {
            ...state,
            changeUserProfileRequest: {
                ...state.changeUserProfileRequest,
                status: RequestStatus.SUCCESS,
                error: null,
            },
            auth: {
                ...state.auth,
                ...auth,
            },
            selectedProfile: currentUserProfileId,
        };
    },
    [CHANGE_USER_PROFILE_FAILURE](
        state: UserState,
        { payload: { error } }: ReturnType<typeof changeUserProfileFailure>,
    ): UserState {
        return {
            ...state,
            changeUserProfileRequest: {
                ...state.changeUserProfileRequest,
                status: RequestStatus.FAILURE,
                error,
            },
        };
    },

    [ADD_PROFILE_REQUEST](state: UserState): UserState {
        return {
            ...state,
            addProfileRequest: {
                ...state.addProfileRequest,
                status: RequestStatus.PENDING,
                error: null,
            },
        };
    },
    [ADD_PROFILE_SUCCESS](
        state: UserState,
        {
            payload: { currentUserProfileId },
        }: ReturnType<typeof addProfileSuccess>,
    ): UserState {
        return {
            ...state,
            addProfileRequest: {
                ...state.addProfileRequest,
                status: RequestStatus.SUCCESS,
                error: null,
            },
            selectedProfile: currentUserProfileId,
        };
    },
    [ADD_PROFILE_FAILURE](
        state: UserState,
        { payload: { error } }: ReturnType<typeof addProfileFailure>,
    ): UserState {
        return {
            ...state,
            addProfileRequest: {
                ...state.addProfileRequest,
                status: RequestStatus.FAILURE,
                error,
            },
        };
    },
    [SELECT_USER_PROFILE_RATING](
        state: UserState,
        { payload }: ReturnType<typeof changeUserProfileRating>,
    ): UserState {
        return {
            ...state,
            profileParentalRating: payload,
        };
    },
    [SELECT_USER_PROFILE_AVATAR](
        state: UserState,
        { payload }: ReturnType<typeof changeUserProfileAvatar>,
    ): UserState {
        return {
            ...state,
            profileAvatar: payload,
        };
    },

    [EDIT_PROFILE_REQUEST](state: UserState): UserState {
        return {
            ...state,
            editProfileRequest: {
                ...state.editProfileRequest,
                status: RequestStatus.PENDING,
                error: null,
            },
        };
    },
    [EDIT_PROFILE_SUCCESS](state: UserState): UserState {
        return {
            ...state,
            editProfileRequest: {
                ...state.editProfileRequest,
                status: RequestStatus.SUCCESS,
                error: null,
            },
        };
    },
    [EDIT_PROFILE_FAILURE](
        state: UserState,
        { payload: { error } }: ReturnType<typeof addProfileFailure>,
    ): UserState {
        return {
            ...state,
            editProfileRequest: {
                ...state.editProfileRequest,
                status: RequestStatus.FAILURE,
                error,
            },
        };
    },
    [EDIT_PROFILE_REQUEST_RESET](state: UserState): UserState {
        return {
            ...state,
            editProfileRequest: {
                ...state.editProfileRequest,
                status: RequestStatus.IDLE,
                error: null,
            },
        };
    },

    [DELETE_PROFILE_REQUEST](state: UserState): UserState {
        return {
            ...state,
            deleteProfileRequest: {
                ...state.deleteProfileRequest,
                status: RequestStatus.PENDING,
                error: null,
            },
        };
    },
    [DELETE_PROFILE_SUCCESS](state: UserState): UserState {
        return {
            ...state,
            deleteProfileRequest: {
                ...state.deleteProfileRequest,
                status: RequestStatus.SUCCESS,
                error: null,
            },
        };
    },
    [DELETE_PROFILE_FAILURE](
        state: UserState,
        { payload: { error } }: ReturnType<typeof addProfileFailure>,
    ): UserState {
        return {
            ...state,
            deleteProfileRequest: {
                ...state.deleteProfileRequest,
                status: RequestStatus.FAILURE,
                error,
            },
        };
    },
    [DELETE_PROFILE_REQUEST_RESET](state: UserState): UserState {
        return {
            ...state,
            deleteProfileRequest: {
                ...state.deleteProfileRequest,
                status: RequestStatus.IDLE,
                error: null,
            },
        };
    },
    [SET_CURRENT_PROFILE_AVATAR](
        state: UserState,
        { payload }: ReturnType<typeof setCurrentProfileAvatar>,
    ): UserState {
        return {
            ...state,
            currentProfileAvatar: payload,
        };
    },
    [SET_OTT_LAST_CONNECTION](
        state: UserState,
        { payload }: ReturnType<typeof setOTTLastConnection>,
    ): UserState {
        return {
            ...state,
            OTTLastConnectionTime: payload,
        };
    },
    [SET_LAST_PROVISION_DATE](
        state: UserState,
        { payload }: ReturnType<typeof setLastProvisionDate>,
    ): UserState {
        return {
            ...state,
            lastProvisionDate: payload,
        };
    },

    [GET_PROFILE_SUCCESS](
        state: UserState,
        { payload: { profile } }: ReturnType<typeof getProfileSuccess>,
    ): UserState {
        return {
            ...state,
            profilesListRequest: {
                ...state.profilesListRequest,
                data: state.profilesListRequest.data.map((_profile) => {
                    if (_profile._id === profile._id) {
                        return profile;
                    }

                    return _profile;
                }),
            },
        };
    },

    [SET_SUSPENSION_TYPE](
        state: UserState,
        { payload: { suspension } }: ReturnType<typeof setSuspensionType>,
    ): UserState {
        return {
            ...state,
            suspension,
        };
    },

    [RESET_CLIENT_ID](state: UserState): UserState {
        return {
            ...state,
            clientId: undefined,
        };
    },

    [SET_CASHED_PIN](
        state: UserState,
        {
            payload: { profileId, pinLimit, timestamp },
        }: ReturnType<typeof setCashedPin>,
    ): UserState {
        if (pinLimit) {
            return {
                ...state,
                cashedPinByParentalRating: {
                    ...state.cashedPinByParentalRating,
                    [profileId]: { [pinLimit]: timestamp },
                },
            };
        }

        return state;
    },
    [RESET_CASHED_PINS](state: UserState): UserState {
        return {
            ...state,
            cashedPinByParentalRating: undefined,
        };
    },
    [SET_LINEUP_CHANGED](
        state: UserState,
        { payload: { isLineupChanged } }: ReturnType<typeof setLineupChanged>,
    ): UserState {
        return {
            ...state,
            isLineupChanged,
        };
    },
    [SET_SHOW_LINEUP_MODAL](
        state: UserState,
        {
            payload: { shouldLineupModalShow },
        }: ReturnType<typeof setShowLineupModal>,
    ): UserState {
        return {
            ...state,
            shouldLineupModalShow,
        };
    },
    [SET_CONNECTION_TYPE](
        state: UserState,
        { payload: { connectionType } }: ReturnType<typeof setConnectionType>,
    ): UserState {
        return {
            ...state,
            connectionType,
        };
    },
    [ACCOUNT_INFO_SUCCESS](
        state: UserState,
        {
            payload: { sharedConsentPublicId },
        }: ReturnType<typeof accountInfoSuccess>,
    ): UserState {
        return {
            ...state,
            sharedConsentPublicId,
        };
    },
};

export default { initialState, handlers };
